import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { sopsAPI } from '../lib/api';
import { 
  FileQuestion, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Trophy,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  Clock,
  Target
} from 'lucide-react';
import { toast } from 'sonner';

const SOPQuizModal = ({ 
  isOpen, 
  onClose, 
  sop, 
  onQuizPassed 
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [result, setResult] = useState(null);
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    if (isOpen && sop) {
      loadQuiz();
    }
  }, [isOpen, sop]);

  const loadQuiz = async () => {
    setLoading(true);
    setResult(null);
    setAnswers({});
    setCurrentQuestion(0);
    setShowReview(false);
    
    try {
      const response = await sopsAPI.getQuiz(sop.id);
      setQuizData(response.data);
      
      // If already passed, show the result
      if (response.data.passed) {
        setResult({
          passed: true,
          score: response.data.attempts?.[0]?.score || 100,
          message: 'You have already passed this quiz!'
        });
      }
    } catch (error) {
      console.error('Failed to load quiz:', error);
      toast.error('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = async () => {
    const quiz = quizData?.quiz;
    if (!quiz) return;

    // Check if all questions are answered
    const unanswered = quiz.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    try {
      const answersList = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer
      }));

      const response = await sopsAPI.submitQuiz(sop.id, answersList);
      
      setResult({
        passed: response.data.passed,
        score: response.data.score,
        earned_points: response.data.earned_points,
        total_points: response.data.total_points,
        passing_score: response.data.passing_score
      });

      if (response.data.passed) {
        toast.success('Congratulations! You passed the quiz!');
        if (onQuizPassed) {
          onQuizPassed(sop.id);
        }
      } else {
        toast.error(`Score: ${response.data.score}%. You need ${response.data.passing_score}% to pass.`);
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to submit quiz. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setResult(null);
    setAnswers({});
    setCurrentQuestion(0);
    setShowReview(false);
  };

  const quiz = quizData?.quiz;
  const questions = quiz?.questions || [];
  const currentQ = questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col" data-testid="sop-quiz-modal">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileQuestion className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="font-heading text-lg">
                {quiz?.title || 'SOP Quiz'}
              </DialogTitle>
              <p className="text-sm text-slate-500">
                {sop?.sop_number} - Pass score: {quiz?.passing_score || 80}%
              </p>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : !quiz ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
            <p className="text-slate-600">No quiz available for this SOP</p>
            <Button variant="outline" onClick={onClose} className="mt-4">Close</Button>
          </div>
        ) : result ? (
          // Result Screen
          <div className="py-6">
            <div className={`text-center p-6 rounded-xl ${
              result.passed ? 'bg-emerald-50' : 'bg-red-50'
            }`}>
              {result.passed ? (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                    <Trophy className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-800">Congratulations!</h3>
                  <p className="text-emerald-600 mt-1">You passed the quiz</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-red-800">Not Quite</h3>
                  <p className="text-red-600 mt-1">You didn't meet the passing score</p>
                </>
              )}
              
              <div className="mt-6 p-4 bg-white rounded-lg">
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-slate-900">{result.score}%</p>
                    <p className="text-sm text-slate-500">Your Score</p>
                  </div>
                  <div className="w-px h-12 bg-slate-200" />
                  <div className="text-center">
                    <p className="text-3xl font-bold text-slate-900">{result.passing_score || quiz?.passing_score}%</p>
                    <p className="text-sm text-slate-500">Required</p>
                  </div>
                </div>
              </div>

              {result.earned_points !== undefined && (
                <p className="text-sm text-slate-500 mt-3">
                  Points: {result.earned_points} / {result.total_points}
                </p>
              )}
            </div>

            <div className="flex justify-center gap-3 mt-6">
              {!result.passed && (
                <Button 
                  onClick={handleRetake}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="retake-quiz-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              )}
              <Button 
                variant={result.passed ? "default" : "outline"}
                onClick={onClose}
                className={result.passed ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              >
                {result.passed ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Continue
                  </>
                ) : (
                  'Close'
                )}
              </Button>
            </div>

            {/* Previous Attempts */}
            {quizData?.attempts?.length > 0 && (
              <div className="mt-6 border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Previous Attempts:</p>
                <div className="space-y-1">
                  {quizData.attempts.slice(0, 5).map((attempt, idx) => (
                    <div key={attempt.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">
                        {new Date(attempt.completed_at).toLocaleDateString()}
                      </span>
                      <Badge className={attempt.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {attempt.score}% - {attempt.passed ? 'Passed' : 'Failed'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Quiz Questions
          <div className="flex flex-col flex-1 min-h-0">
            {/* Progress */}
            <div className="mb-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">
                  Question {currentQuestion + 1} of {questions.length}
                </span>
                <span className="text-sm text-slate-600">
                  {answeredCount} answered
                </span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Question - Scrollable area */}
            <ScrollArea className="flex-1 min-h-0 max-h-[40vh] pr-4">
              {currentQ && (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900">{currentQ.question_text}</p>
                  </div>

                  <RadioGroup
                    value={answers[currentQ.id] || ''}
                    onValueChange={(value) => handleAnswerChange(currentQ.id, value)}
                    className="space-y-2"
                  >
                    {currentQ.options?.map((option, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          answers[currentQ.id] === option 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleAnswerChange(currentQ.id, option);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          handleAnswerChange(currentQ.id, option);
                        }}
                      >
                        <RadioGroupItem value={option} id={`option-${currentQ.id}-${idx}`} />
                        <Label htmlFor={`option-${currentQ.id}-${idx}`} className="flex-1 cursor-pointer pointer-events-none">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {/* Question Navigation Dots */}
              <div className="flex flex-wrap gap-2 mt-6 justify-center">
                {questions.map((q, idx) => (
                  <button
                    key={q.id}
                    onClick={() => setCurrentQuestion(idx)}
                    className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      idx === currentQuestion
                        ? 'bg-blue-600 text-white'
                        : answers[q.id]
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`question-nav-${idx}`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </ScrollArea>

            {/* Footer - Always visible */}
            <DialogFooter className="flex justify-between mt-4 pt-4 border-t flex-shrink-0">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestion === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentQuestion(prev => Math.min(questions.length - 1, prev + 1))}
                  disabled={currentQuestion === questions.length - 1}
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              <Button
                onClick={handleSubmit}
                disabled={submitting || answeredCount < questions.length}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-quiz-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Target className="w-4 h-4 mr-2" />
                    Submit Quiz ({answeredCount}/{questions.length})
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SOPQuizModal;
